/**
 * Shared formatting utilities
 *
 * Currency and date formatting using business settings (currency, timezone).
 * Import these in pages/components instead of defining local formatCurrency/formatDate.
 */

import { locale } from '@/shared/i18n';
import { t } from '@/shared/i18n';

/** Map app locale to Intl locale string */
export function getIntlLocale(): string {
  return locale() === 'vi' ? 'vi-VN' : 'en-US';
}

/**
 * Format a number as currency using the given currency code.
 * Falls back to 'USD' if not provided.
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD'
): string {
  const intlLocale = getIntlLocale();
  try {
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    // Fallback if currency code is invalid
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
}

/**
 * Format a date string using the given timezone.
 * Falls back to 'UTC' if not provided.
 */
export function formatDate(
  dateString: string,
  timezone: string = 'UTC',
  options?: Intl.DateTimeFormatOptions
): string {
  const date = new Date(dateString);
  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
    ...options,
  };
  const intlLocale = getIntlLocale();
  try {
    return date.toLocaleString(intlLocale, defaultOptions);
  } catch {
    // Fallback if timezone is invalid
    return date.toLocaleString(intlLocale, {
      ...defaultOptions,
      timeZone: 'UTC',
    });
  }
}

/**
 * Format a quantity for display — integers show without decimals,
 * decimals show up to 2 places with trailing zeros trimmed.
 */
export function formatQuantity(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return parseFloat(value.toFixed(2)).toString();
}

/**
 * Format a date as a short relative or absolute string.
 * Uses timezone for the absolute fallback.
 */
export function formatRelativeDate(
  dateString: string,
  timezone: string = 'UTC'
): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return t('common.justNow');
  if (diffMins < 60) return t('common.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('common.hoursAgo', { count: diffHours });
  if (diffDays < 7) return t('common.daysAgo', { count: diffDays });

  return formatDate(dateString, timezone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
